/**
 * Privacy Policy — DRAFT for lawyer review. Client-facing text only.
 *
 * REVISED 29 Sep 2026 (owner review). Three changes of principle:
 *
 *   1. NO INTERNAL NOTES IN THE CLIENT TEXT. The 16 Sep draft carried bracketed notes addressed to
 *      the lawyer, the owner and the engineer ("[LAWYER: confirm …]") inside the body, and the
 *      body is rendered verbatim, so every client who opened the policy read them. All of them now
 *      live in docs/legal/README.md ("Open items moved out of the client-facing text").
 *   2. PROVIDERS BY ROLE AND COUNTRY, NOT BY NAME. APP 1.4(f)–(g) requires us to say whether we are
 *      likely to disclose overseas and, if practicable, the countries — not to name vendors. Naming
 *      each supplier added nothing a client can act on, went stale whenever a supplier changed,
 *      and invited the reading that we endorse them. The internal register of named providers is
 *      in docs/legal/README.md.
 *   3. ONLY WHAT THE CODE DOES. A fact-check against the code on 29 Sep corrected: two-factor is
 *      required for firm staff and optional for individuals (not "all accounts"); firms are not
 *      asked for a phone number; sign-in throttling is per internet address; the data download
 *      covers account and planning information, not "everything"; the anonymous assistant counts
 *      by internet address; the storage city is not confirmed, so the policy says Australia; fonts
 *      and images loaded from third-party services were undisclosed; payments are not yet live.
 *
 * APP 1.4 structure: (a) kinds, (b) how collected and held, (c) purposes, (d) access and
 * correction, (e) complaints, (f)/(g) overseas disclosure and countries — plus sensitive
 * information (APP 3.3), collection from others (APP 5), direct marketing (APP 7), government
 * identifiers (APP 9), security and retention (APP 11), Notifiable Data Breaches, and the automated
 * decision-making statement (APP 1.7–1.9, from 10 December 2026).
 *
 * `version` must match server/src/shared/legal/legalVersions.ts.
 */
export const PRIVACY_POLICY = {
  key: 'privacy_policy',
  version: '2026-10-15',
  title: 'Privacy Policy',
  effectiveDate: '15 October 2026',
  draft: true,
  body: `
## 1. About this policy

This policy explains how AgedCareWise ("we", "us") collects, holds, uses and discloses personal
information, and how you can access and correct it or make a complaint. We are bound by the
Privacy Act 1988 (Cth) and the Australian Privacy Principles.

It covers the AgedCareWise website and application, the AWise assistant, and our dealings with
the people who use them: individuals planning for themselves or a family member, the
professional firms that use AgedCareWise with their clients, and those firms' staff.

**Privacy contact:** privacy@agedcarewise.com.au. Write to us with any question, request or
complaint about privacy.

## 2. The kinds of personal information we collect and hold

**If you are planning for yourself or someone in your family:**

- **Identity and contact details:** name, email address, date of birth, sex (used only to apply
  the right life-expectancy tables) and, if you choose to give it, a preferred phone number. If
  someone is completing the profile on the person's behalf (for example under an enduring power
  of attorney), that person's name and relationship to them.
- **Household and living arrangements:** whether the plan is for one person or a couple, the
  partner's details, where each person lives now (own home, renting, with family, a retirement
  village, a granny-flat arrangement or residential care) and any related agreements.
- **Financial details:** the family home and other property, bank accounts, shares and
  investments, superannuation and pensions, annuities, trusts and companies, loans and other
  debts, income (including any government or veterans' payments), living expenses, gifts made,
  and insurance held.
- **Family and estate matters:** the number of children, family living arrangements and what the
  person would like to happen to their assets.
- **Care needs and health — sensitive information:** a general rating of health (good, fair or
  poor), whether private health insurance is held, the outcome of an aged-care assessment and the
  level of care recommended, and the care and living goals being considered. We do not ask for
  medical conditions, diagnoses, medications or medical records. See section 4.
- **Plans and advice records:** the strategies modelled, the plans saved, and any Statement of
  Advice produced.

**If you work for a professional firm that uses AgedCareWise:** your name, work email address,
role, your firm's details (name, ABN and licence details where applicable) and your firm's
subscription records.

**Everyone with an account:** email address and password (stored only in a form that cannot be
reversed), two-factor authentication settings, sign-in and security events, the date, time,
internet (IP) address and browser type recorded when you accept our terms, a record of actions
taken in your account (an audit trail), payment records, your correspondence with us, and the
messages you send to the AWise assistant.

**We do not ask for government identifiers** such as a tax file number, Centrelink customer
reference number or Medicare number, and you should not enter them in free-text fields.

**Anonymous use.** You can try the AWise assistant on our website without an account. We do not
know who you are. To limit free use we keep a short-lived count of messages against your internet
address, and your message is sent to our AI service provider to produce the reply (section 6).

## 3. How we collect personal information

- **From you**, when you create an account, complete your profile, build plans, ask the AWise
  assistant a question, pay for the service or contact us — including any financial document you
  choose to upload (such as a super, pension, bank or share statement) so that your profile can
  be drafted from it.
- **From someone else — a professional firm that is helping you.** Where a firm has invited you, it may enter
  some or all of your details before you first sign in. When that happens we tell you the first
  time you open your planning link, and everything the firm entered is there for you to review
  and correct.
- **From a person acting for you**, such as an attorney or family member completing the profile
  on your behalf, who confirms they are authorised to do so.
- **Automatically**, as service data: sign-in events, security logs and the audit trail described
  in section 2. We do not use advertising or analytics trackers.
- **From our payment provider**, which tells us that a payment succeeded and gives us a reference.
  Card details go to the payment provider directly and never reach our systems.

If we receive personal information we did not ask for and could not lawfully have collected
ourselves, we destroy or de-identify it as soon as practicable.

## 4. Sensitive information (health)

Care-needs and health details are **sensitive information**. We ask only for a general picture:
how you would rate your health overall (good, fair or poor) and the outcome of any aged-care
assessment. We do not ask about specific medical conditions, diagnoses, medications or medical
records, and you do not need to give them to us.

We ask for these details for two reasons:

- **Your general health** helps us judge whether a strategy suits you. Some strategies tie money
  up or take many years to pay off, such as a lifetime annuity that returns little to your
  estate. When your general health suggests one of these may not suit you, we point that out, so
  you can weigh how much it matters to keep your money within easy reach. Your health rating does
  not change the figures in your plan.
- **Your aged-care assessment** sets the care you are approved for, which in turn sets the fees
  and government support that apply. We need it to model those costs.

We collect these details **only with your consent** and only for these purposes. You give that
consent by entering those details yourself, or by asking a firm or a person acting for you to
enter them. A firm that enters them tells us it has your consent to do so.

You may leave these fields blank. If you do, we make no assumption about your health, and the
modelling of care eligibility, care fees and related strategies will be limited or unavailable.
We do not use health information for any other purpose and do not disclose it except as set out
in section 6.

## 5. Why we collect, hold, use and disclose personal information

- **To provide the service you have asked for:** modelling your options, producing projections,
  strategy comparisons, plans and Statements of Advice, and answering your questions through the
  AWise assistant.
- **To run and secure your account:** signing you in, two-factor authentication, detecting and
  preventing unauthorised access, and keeping the audit trail.
- **To work with your professional firm**, where one has invited you, so that it can see and
  work on the file it is helping you with.
- **To take payment** and manage subscriptions.
- **To send service messages:** verification codes, password resets, invitations and — where a
  firm has asked us to — a reminder that a planning link is waiting. These are not marketing.
  We do not send text messages.
- **To meet our legal obligations**, including financial-services record-keeping and responding
  to lawful requests.
- **To supervise the advice we are responsible for.** Where AgedCareWise provides the advice, either
  directly or through a firm that is not itself licensed, people we authorise review advice records
  to check the quality of the advice and to handle complaints. Each review is recorded. We do not
  review the clients of firms that give advice under their own licence.
- **To improve the service**, using information in de-identified or aggregated form wherever
  possible, and to investigate and fix faults. To understand how the service is used, we count
  things such as the number of client files and Statements of Advice. For clients we advise under
  our own licence, we also keep a de-identified summary of each file: which goals were chosen,
  single or couple, an age band, and broad bands for home value, super, net wealth and living
  expenses. It holds no names, contact details, dates of birth or exact amounts, and we only ever
  look at groups of at least ten people.

**We do not sell personal information, use it for direct marketing, or share it with anyone for
advertising.** If we ever wish to send you marketing, we will ask first, and you will be able to
opt out at any time.

If you choose not to provide the information we ask for, we may be unable to create your
account, and any modelling will be incomplete or unavailable.

## 6. Who we disclose personal information to

- **Your professional firm.** If a firm invited you, its authorised staff see and work on your
  file. The firm is a separate organisation with its own obligations under the Privacy Act; its
  handling of your information is described in its own privacy policy.
- **A person acting for you**, where you or the law have authorised them.
- **Service providers who help us run AgedCareWise**, each of which receives only what it needs
  for its task and is bound to protect it:
  - **Email delivery** — sends our service emails. It receives your email address and the
    message only.
  - **Payment processing** — takes card payments when you pay for the service. Your card details
    go to it directly.
  - **AI service** — generates the AWise assistant's replies. Your chat messages are sent to it
    for that purpose. If you ask the assistant about your own saved plan, we also send a short
    summary of that plan's figures — such as the annual cost, Age Pension, cash flow and net worth
    by year — but never your name, date of birth, contact details or the underlying profile.
    If you upload a financial document to pre-fill your profile, that document is sent to the AI
    service to draft the entries, which you then review before anything is saved. The AI service
    does not keep the document, and we do not store it either. Please cover or leave out any tax
    file number before uploading.
- **Regulators, courts and law-enforcement bodies** where the law requires or authorises it.
- **Our professional advisers** (lawyers, auditors) under a duty of confidentiality.
- **A purchaser of our business**, if the business or its assets are sold, with notice to you.

## 7. Overseas disclosure

Your profile, plans, advice records and account are held on servers in Australia. Some of the
service providers above are located, or process information, outside Australia: the AI service,
payment processing and email delivery. The countries where these
recipients are likely to be located are the **United States** and **Japan**. Before disclosing
personal information to an overseas recipient we take reasonable steps, including contractual
terms, to ensure it handles the information consistently with the Australian Privacy Principles.

## 8. How we hold and protect personal information

Your profile, plans and advice records are **stored in Australia**. Personal and financial
details are encrypted before they are stored, on top of encryption of the storage itself. Each
firm's workspace is kept separate, so no firm can see another's clients.

Two-factor authentication with an authenticator app is **required for professional firm staff**
and **available to everyone else** — we recommend you turn it on. Repeated sign-in attempts are
slowed or blocked, every significant action is written to a tamper-evident audit trail, and only
staff who need access for a specific task are given it. The system is independently
security-tested before it is launched for public use.

## 9. How long we keep it

- **While your account exists.** If your access ends, your information is kept, locked, so that
  you can return to it.
- **Advice we give you is kept for at least 7 years** as financial-services law requires, even
  if you close your account or ask us to delete your information. The advice record is each
  Statement of Advice we gave you and the information about you that it was based on. It is
  locked, used only to meet our legal obligations, and deleted when the period ends.
- **Advice a professional firm gives you** is the firm's record. The firm decides how long it is
  kept, and we keep or delete it on the firm's instructions.
- **Records of your acceptance of our terms, and the audit trail,** are kept for as long as we may
  need to show what was agreed and what was done.
- When information is no longer needed for any purpose for which it may be used or disclosed, and
  we are not required to keep it, we delete or de-identify it.

## 10. Automated decision-making

The Privacy Act requires us to say how computer programs use personal information to make, or
help make, decisions that could reasonably be expected to significantly affect your rights or
interests.

- **The kinds of personal information used:** the planning information described in section 2 —
  your household, financial, family and estate details, and (with your consent) your care-needs
  and health details — and your account security data.
- **Decisions made solely by a computer program:** our systems automatically slow or temporarily
  block sign-in attempts and requests that look like an attack (for example, repeated failed
  passwords). No other decision about you is made solely by a program.
- **Programs that do things substantially and directly related to making a decision:** our
  modelling engine produces projections, ranks strategies and generates any Statement of Advice
  from the information you enter. These outputs are the substance of the service and can
  significantly influence the decisions you make. Where a professional firm advises you, its
  adviser reviews the outputs with you. The AWise assistant provides general and educational
  answers only and makes no decision about you.

You can ask us how any of these outputs was produced, and you can correct the information they
were produced from at any time.

## 11. Access and correction

- **Access:** download a copy of your account details and your planning information from your
  account settings at any time. For anything else we hold about you — such as your terms
  acceptances, security records or assistant conversations — ask us at
  privacy@agedcarewise.com.au. We may need to verify your identity first.
- **Correction:** most planning details you can correct yourself in the app. For anything else,
  ask us and we will correct it, or tell you in writing why we will not and how to complain.
- **Deletion:** request deletion from your account settings or by email, and we will action it
  within 30 days.
  - If we have never given you a Statement of Advice, we delete all of your information.
  - If we have, we keep the advice record described in section 9 and delete everything else:
    your sign-in and security details, your assistant conversations, plans and scenarios that
    did not form part of the advice, and contact details we no longer need. We will tell you
    what we have kept and when it will be deleted.
  - If a professional firm advises you, your file is part of the firm's records. Ask the firm
    first; if you ask us, we will pass your request to the firm. We delete a firm's client file
    when the firm instructs us to.

We respond to access and correction requests within 30 days and do not charge for them.

## 12. Complaints

Write to privacy@agedcarewise.com.au. We will acknowledge your complaint promptly, investigate,
and respond within 30 days. If you are not satisfied with our response you may complain to the
**Office of the Australian Information Commissioner** (oaic.gov.au, 1300 363 992).

## 13. Data breaches

If a data breach is likely to result in serious harm to you, we will notify you and the Office of
the Australian Information Commissioner promptly under the Notifiable Data Breaches scheme, tell
you what happened and what information was involved, and give you clear recommended steps.

## 14. Website, cookies and browser storage

We do not use advertising or analytics cookies. Your browser stores a sign-in token and a few
preferences (for example, a dismissed notice) so the application works; these are not shared
with anyone. Our servers keep standard technical logs, including internet addresses, for
security.

## 15. Changes to this policy

We will post updates here with a new version date. For material changes we will ask you to
acknowledge the updated policy when you next sign in.
`,
};

export default PRIVACY_POLICY;
